<template>
  <div id="user-list" class="car-feature">
    <CarFeatureAddNew
      v-model="isAddNewCarFeatureSidebarActive"
      :id="carFeatureId"
      :type-options="typeOptions"
      @reset-id="carFeatureId = null"
    ></CarFeatureAddNew>

    <!-- list filters -->
    <v-card>
      <v-card-title class="d-flex">
        <div class="mr-auto">
          Car Features
          <!-- <span class="car-feature-count mt-1 ml-1">({{ filteredCarFeatures.length +  '/' + carFeatures.length }})</span> -->
        </div>
        <div class="d-flex">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search"
            outlined
            hide-details
            dense
            clearable
            class="text-search me-3 mb-4"
          >
          </v-text-field>
          <v-btn icon class="mb-4" @click="showFilters = !showFilters">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon size="24" class="me-1" v-bind="attrs" v-on="on">
                  {{ icons.mdiFilter }}
                </v-icon>
              </template>
              <span>Filters</span>
            </v-tooltip>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon dark v-bind="attrs" v-on="on">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon size="24" class="me-1" v-bind="attrs" v-on="on">
                      {{ icons.mdiSort }}
                    </v-icon>
                  </template>
                  <span>Sort By</span>
                </v-tooltip>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="(item, index) in sortOptions" :key="index" @click="sortBy = item.value">
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- <v-btn
            color="primary"
            class="mb-4 ml-3"
            @click.stop="addCarFeature"
          >
            <v-icon
              size="17"
              class="me-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            <span>Add</span>
          </v-btn> -->
        </div>
      </v-card-title>

      <!-- actions -->
      <v-card-text class="d-flex flex-wrap pb-0" v-if="showFilters">
        <!-- search -->
        <v-select
          v-model="statusFilter"
          placeholder="Select Status"
          :items="statusOptions"
          item-text="title"
          item-value="value"
          outlined
          clearable
          dense
          hide-details
          class="user-search me-3 mb-4"
        ></v-select>
      </v-card-text>

      <p class="ml-2">{{ filteredCarFeatures.length || 'No' }} records found</p>
      <v-row>
        <v-col cols="12" lg="3">
          <AddNewCard title="Add Car Feature" textSize="text-h6" @click="addCarFeature" />
        </v-col>
        <v-col
          cols="12"
          lg="3"
          v-for="({ id, description, featureicon, active, markltype, updatedon }, c) in paginatedCarFeatures[page - 1]"
          :key="c"
        >
          <CarFeatureCard
            :id="id"
            :featureName="description"
            :image="featureicon"
            :isActive="active"
            :type="markltype"
            :updatedOn="formatDate(updatedon.toDate(), 'DD-MMM-YYYY hh:mm A')"
            @edit-carfeature="editCarFeature"
          />
        </v-col>
      </v-row>

      <div class="d-flex">
        <div class="mt-2 mr-auto ml-5">
          <span>Items per page: {{ itemsPerPage }} </span>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-icon dark v-bind="attrs" v-on="on">
                {{ icons.mdiMenuDown }}
              </v-icon>
            </template>
            <v-list>
              <v-list-item v-for="(item, index) in itemsPerPageOptions" :key="index" @click="itemsPerPage = item.title">
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="d-flex">
          <span class="mt-2">{{ countText }}</span>
          <v-pagination v-model="page" :length="paginatedCarFeatures.length"></v-pagination>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { ref, onUnmounted, computed } from '@vue/composition-api'
import { mdiPlus, mdiChevronLeft, mdiChevronRight, mdiFilter, mdiSort, mdiMenuDown } from '@mdi/js'
import useCarFeature from './useCarFeature'
import useMoment from './../../../composables/useMoment'
import CarFeatureCard from './CarFeatureCard.vue'
import CarFeatureAddNew from './CarFeatureAddNew.vue'
import AddNewCard from '@/components/AddNewCard.vue'

export default {
  components: {
    CarFeatureCard,
    CarFeatureAddNew,
    AddNewCard,
  },
  setup() {
    const isAddNewCarFeatureSidebarActive = ref(false)
    const carFeatureId = ref(null)
    const showFilters = ref(true)
    const statusOptions = [
      { title: 'Active', value: true },
      { title: 'Inactive', value: false },
    ]

    const typeOptions = [
      { title: 'Luxury', value: 'Luxury' },
      { title: 'Sports Car', value: 'Sports Car' },
      { title: 'Executive', value: 'Executive' },
      { title: 'Super Car', value: 'Super Car' },
      { title: 'Standard', value: 'Standard' },
    ]

    const sortOptions = [
      { title: 'Last Updated', value: { by: 'updatedon', order: 'desc' } },
      { title: 'First Updated', value: { by: 'updatedon', order: 'asc' } },
      { title: 'A to Z', value: { by: 'description', order: 'asc' } },
      { title: 'Z to A', value: { by: 'description', order: 'desc' } },
    ]

    const itemsPerPageOptions = [{ title: '16' }, { title: '32' }, { title: '48' }, { title: 'All' }]

    const {
      page,
      itemsPerPage,
      carFeatures,
      filteredCarFeatures,
      paginatedCarFeatures,
      searchQuery,
      statusFilter,
      typeFilter,
      sortBy,
      loading,

      unsubscribeCarFeatures,
    } = useCarFeature()

    const countText = computed(() => {
      if (paginatedCarFeatures.value.length) {
        const previous = itemsPerPage.value == 'All' ? 0 : (page.value - 1) * itemsPerPage.value
        const start = previous + 1
        const end = previous + paginatedCarFeatures.value[page.value - 1].length
        return `${start}-${end} of ${filteredCarFeatures.value.length}`
      }
      return ''
    })

    const { formatDate } = useMoment()

    const addCarFeature = () => {
      carFeatureId.value = null
      isAddNewCarFeatureSidebarActive.value = !isAddNewCarFeatureSidebarActive.value
    }

    const editCarFeature = id => {
      carFeatureId.value = id
      isAddNewCarFeatureSidebarActive.value = !isAddNewCarFeatureSidebarActive.value
    }

    onUnmounted(() => {
      unsubscribeCarFeatures()
    })

    return {
      page,
      itemsPerPage,
      carFeatureId,
      showFilters,
      isAddNewCarFeatureSidebarActive,
      statusOptions,
      typeOptions,
      carFeatures,
      filteredCarFeatures,
      paginatedCarFeatures,
      searchQuery,
      statusFilter,
      typeFilter,
      sortBy,
      sortOptions,
      loading,
      formatDate,
      editCarFeature,
      addCarFeature,
      itemsPerPageOptions,
      countText,

      // icons
      icons: {
        mdiPlus,
        mdiChevronLeft,
        mdiChevronRight,
        mdiFilter,
        mdiSort,
        mdiMenuDown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.car-feature {
  .car-feature-count {
    font-size: 1rem;
  }
  .v-input {
    max-width: 200px;
  }
  .text-search {
    min-width: 300px;
  }
}
</style>
